import React from 'react'

import { Box, Paper, Typography, TextField, FormControlLabel, Switch, Link } from '@mui/material'

import { IProject } from '../../../api/Data'

const styles = {
  paper: {
    padding: '16px',
  },
}

const formatUrl = (url?: string) => {
  if (!url) return ''

  return url.startsWith('http') ? url : `https://${url}`
}

const LegalComplianceCard = ({ project, handleChange }: IProps) => {
  const useWorkspaceLegalSettings = project.useTeamLegalSettings !== false

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e, { name: e.target.name, value: e.target.value })
  }

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e, { name: e.target.name, value: e.target.checked })
  }

  return (
    <Paper sx={styles.paper}>
      <Typography variant="h5" fontWeight="bold">
        Legal & Compliance
      </Typography>

      <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 1 }}>
        Use workspace legal defaults or set client-specific legal details for this project.
      </Typography>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Need help?{' '}
        <Link
          href="https://vloggi.com/docs/how-to-set-legal-entity-privacy-and-declaration-details-in-vloggi/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Read how to set legal entity, privacy and declaration details.
        </Link>
      </Typography>

      <FormControlLabel
        label="Use workspace legal settings"
        control={
          <Switch
            checked={useWorkspaceLegalSettings}
            onChange={handleSwitchChange}
            name="useTeamLegalSettings"
            sx={{
              '& .MuiSwitch-switchBase': {
                color: '#6b50ff !important',
              },
            }}
          />
        }
      />

      {!useWorkspaceLegalSettings && (
        <Box display="flex" flexDirection="column" gap={2} marginTop={2}>
          <TextField
            label="Legal entity name"
            placeholder="e.g. Northstar Energy Holdings LLC"
            name="overrideContentOwner"
            fullWidth
            value={project.overrideContentOwner || ''}
            onChange={handleTextChange}
            helperText="The legal organization responsible for this project. This can differ from the workspace owner."
          />

          <Box display="flex" gap={2} alignItems="center">
            <TextField
              label="Controller privacy policy URL"
              placeholder="https://www.example.com/privacy"
              name="overridePrivacyUrl"
              fullWidth
              value={project.overridePrivacyUrl || ''}
              onChange={handleTextChange}
              helperText="Shown to contributors so they can read how their personal data is processed."
            />

            {project.overridePrivacyUrl && (
              <Link
                href={formatUrl(project.overridePrivacyUrl)}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ whiteSpace: 'nowrap', mb: 3 }}
              >
                Test link
              </Link>
            )}
          </Box>

          <Box display="flex" gap={2} alignItems="center">
            <TextField
              label="Legal disclosure / Impressum URL"
              placeholder="https://www.example.com/impressum"
              name="overrideLegalDisclosureUrl"
              fullWidth
              value={project.overrideLegalDisclosureUrl || ''}
              onChange={handleTextChange}
              helperText="Optional. Used where a formal legal notice or company disclosure page is required."
            />

            {project.overrideLegalDisclosureUrl && (
              <Link
                href={formatUrl(project.overrideLegalDisclosureUrl)}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ whiteSpace: 'nowrap', mb: 3 }}
              >
                Test link
              </Link>
            )}
          </Box>

          <FormControlLabel
            label="Enable additional declaration"
            control={
              <Switch
                checked={!!project.additionalDeclarationEnabled}
                onChange={handleSwitchChange}
                name="additionalDeclarationEnabled"
                sx={{
                  '& .MuiSwitch-switchBase': {
                    color: '#6b50ff !important',
                  },
                }}
              />
            }
          />

          <TextField
            label="Additional declaration text"
            placeholder="e.g. I confirm I have permission from all people shown in this content."
            name="additionalDeclarationText"
            fullWidth
            value={project.additionalDeclarationText || ''}
            onChange={handleTextChange}
            helperText="Adds an extra contributor confirmation checkbox for this project."
          />
        </Box>
      )}
    </Paper>
  )
}

interface IProps {
  project: IProject
  handleChange: (arg: any, data: any) => void
}

export default LegalComplianceCard
